import { MESApiService, spParameter } from "@dexteel/mesf-core";
import moment from "moment";
import type { TaskHistoryEntry } from "../models/types";

/**
 * GET TASK HISTORY
 * Returns the last N tasks for a PartId with neighbor timestamps for validation bounds.
 * Used by the Correct Dates modal.
 *
 * Date fields are parsed with `moment.utc().toDate()` — same pattern as all
 * other EQP repositories. The API returns timezone-naive UTC datetimes that
 * must be explicitly parsed as UTC to avoid timezone-shift bugs downstream.
 */
export const getTaskHistory = async (
  partId: number,
  top: number = 5,
): Promise<TaskHistoryEntry[]> => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "PartId", value: partId },
    { name: "Top", value: top },
  ];
  const response = await apiService.callV2(
    "[RBD].[EQPGetTaskHistory]",
    parameters,
  );
  if (!response.ok) return [];

  const rows = (response.data?.tables?.[0]?.rows ?? []) as Record<
    string,
    unknown
  >[];

  return rows.map((row) => ({
    ...row,
    Timestamp: row.Timestamp
      ? moment.utc(row.Timestamp as string).toDate()
      : new Date(),
    PreviousTimestamp: row.PreviousTimestamp
      ? moment.utc(row.PreviousTimestamp as string).toDate()
      : null,
    NextTimestamp: row.NextTimestamp
      ? moment.utc(row.NextTimestamp as string).toDate()
      : null,
  })) as TaskHistoryEntry[];
};

/**
 * GET TASK HISTORY WITH LOCATION CONTEXT
 * Same as getTaskHistory but also returns the NextOccupantInstallTimestamp
 * from the second result set (for uninstalled parts correcting dates).
 */
export const getTaskHistoryWithLocationContext = async (
  partId: number,
  top: number = 5,
): Promise<{
  tasks: TaskHistoryEntry[];
  nextOccupantInstallTimestamp: Date | null;
}> => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "PartId", value: partId },
    { name: "Top", value: top },
  ];
  const response = await apiService.callV2(
    "[RBD].[EQPGetTaskHistory]",
    parameters,
  );
  if (!response.ok) return { tasks: [], nextOccupantInstallTimestamp: null };

  const rows = (response.data?.tables?.[0]?.rows ?? []) as Record<
    string,
    unknown
  >[];
  const tasks = rows.map((row) => ({
    ...row,
    Timestamp: row.Timestamp
      ? moment.utc(row.Timestamp as string).toDate()
      : new Date(),
    PreviousTimestamp: row.PreviousTimestamp
      ? moment.utc(row.PreviousTimestamp as string).toDate()
      : null,
    NextTimestamp: row.NextTimestamp
      ? moment.utc(row.NextTimestamp as string).toDate()
      : null,
  })) as TaskHistoryEntry[];

  // Second result set: NextOccupantInstallTimestamp
  const locRows = (response.data?.tables?.[1]?.rows ?? []) as Record<
    string,
    unknown
  >[];
  const nextOccupantInstallTimestamp =
    locRows.length > 0 && locRows[0].NextOccupantInstallTimestamp
      ? moment.utc(locRows[0].NextOccupantInstallTimestamp as string).toDate()
      : null;

  return { tasks, nextOccupantInstallTimestamp };
};

/**
 * GET OPERATION BOUNDS
 * Centralized validation: returns the lower and upper boundary for any EQP operation.
 * Used by modals to show consistent date constraints.
 */
export const getOperationBounds = async (
  partId: number,
  action: "install" | "uninstall" | "correct_install" | "correct_uninstall",
  parentPartId?: number | null,
  positionId?: number,
): Promise<{
  lowerBound: Date | null;
  upperBound: Date | null;
  lowerBoundSource: string | null;
  upperBoundSource: string | null;
}> => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "PartId", value: partId },
    { name: "Action", value: action },
  ];
  if (parentPartId != null)
    parameters.push({ name: "ParentPartId", value: parentPartId });
  if (positionId != null)
    parameters.push({ name: "PositionId", value: positionId });

  const response = await apiService.callV2(
    "[RBD].[EQPGetOperationBounds]",
    parameters,
  );
  if (!response.ok)
    return {
      lowerBound: null,
      upperBound: null,
      lowerBoundSource: null,
      upperBoundSource: null,
    };

  const rows = (response.data?.tables?.[0]?.rows ?? []) as Record<
    string,
    unknown
  >[];
  if (rows.length === 0)
    return {
      lowerBound: null,
      upperBound: null,
      lowerBoundSource: null,
      upperBoundSource: null,
    };

  const row = rows[0];
  return {
    lowerBound: row.LowerBound
      ? moment.utc(row.LowerBound as string).toDate()
      : null,
    upperBound: row.UpperBound
      ? moment.utc(row.UpperBound as string).toDate()
      : null,
    lowerBoundSource: (row.LowerBoundSource as string) ?? null,
    upperBoundSource: (row.UpperBoundSource as string) ?? null,
  };
};

/**
 * UPDATE TASK TIMESTAMP
 * Updates a task's timestamp with neighbor-bound validation.
 * Used by the Correct Dates modal inline editor.
 */
export const updateTaskTimestamp = async (
  taskId: number,
  newTimestamp: Date,
  procedureName: string = "[RBD].[EQPUpdateTaskTimestamp]",
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "TaskId", value: taskId },
    { name: "NewTimestamp", value: newTimestamp.toISOString() },
  ];
  return await apiService.callV2(procedureName, parameters);
};

/**
 * SET INITIAL METRIC VALUE
 * Sets the initial metric value (pre-system heats) for a part.
 * When @ApplyToChildren = 0, sets for a single part.
 * When @ApplyToChildren = 1, sets for all installed replaceable descendants + parent.
 */
export const setInitialMetricValue = async (
  partId: number,
  newValue: number,
  applyToChildren: boolean,
  userId: number | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "NewValue", value: newValue });
  parameters.push({ name: "ApplyToChildren", value: applyToChildren });
  parameters.push({ name: "UserId", value: userId });

  return await apiService.callV2(
    "[RBD].[EQPSetInitialMetricValue]",
    parameters,
  );
};
